import {
  CHECKOUT_ORIGEM_STORAGE_KEY,
  CHECKOUT_ORIGEM_TTL_MS,
  CHECKOUT_SUPERFICIE,
  CREDITOS_ROTA_PREFIXO,
} from '@modules/ai-framework/constants/Text'

// De onde o consultor SAIU para a Stripe — para voltar para lá.
//
// O checkout é hospedado pela Stripe e devolve o navegador para rotas FIXAS do backend
// (`usecase_creditos_assinatura.ROTA_RETORNO_ASSINATURA`, `usecase_creditos_compra.
// ROTA_RETORNO_PAGAMENTO`): a página de créditos ou a do pagamento avulso. O POST não
// aceita rota de retorno. Quem assinou pelo MODAL de créditos, de dentro do chat imersivo,
// voltava para `/central-ia/creditos/planos` e perdia a conversa. Aqui fica a memória
// dessa origem, em `sessionStorage` — a Stripe redireciona na MESMA aba, e é a aba que
// tem a sessão.
//
// ★ Duas fases no MESMO registro: `ida` (gravado antes de sair) e `volta` (o guard de
// rota marcou o retorno e redirecionou). A segunda fase existe porque o modal é um
// componente assíncrono de `global-modals.vue`, montado depois do `<Layout>` — o
// `GlobalBus.$emit` do guard pode acontecer antes de ele existir. O `created` do modal
// consome o retorno pendente (`consumirRetorno`) quando o emit se perdeu.
//
// ★ A rota gravada é só PATH INTERNO (`/...`, sem `//`, sem host): o registro fica num
// storage que a página controla, e `router.replace` para o que estiver lá seria um open
// redirect. `rotaInternaValida` é a única porta.
//
// Puro (sem Vue): quem liga ao `$route` é o `fluxoAssinaturaMixin`; quem redireciona é o
// guard (`retorno-do-checkout-guard.js`).
const FASE = { IDA: 'ida', VOLTA: 'volta' }

// Path interno: começa com UMA barra. `//host` é protocol-relative (vai para outro
// domínio) e `/\host` é lido por alguns navegadores do mesmo jeito.
const ROTA_INTERNA = /^\/(?![/\\])/

function rotaInternaValida(rota) {
  return typeof rota === 'string' && ROTA_INTERNA.test(rota)
}

// As rotas da própria página de créditos (a página e o retorno do pagamento). Sair para a
// Stripe DELAS não precisa de memória: a rota de retorno do backend já é a certa.
function rotaEhDeCreditos(nomeDaRota) {
  return typeof nomeDaRota === 'string' && nomeDaRota.indexOf(CREDITOS_ROTA_PREFIXO) === 0
}

// `sessionStorage` pode não existir (SSR, iframe sem storage, modo privado antigo) ou
// lançar no acesso (quota, `SecurityError`). Nenhuma dessas falhas pode impedir a saída
// para o pagamento: quem chama recebe `null`/`false` e segue.
function storage() {
  try {
    return window.sessionStorage || null
  } catch (e) {
    return null
  }
}

function lerRegistro() {
  const area = storage()
  if (!area) return null
  try {
    const bruto = area.getItem(CHECKOUT_ORIGEM_STORAGE_KEY)
    return bruto ? JSON.parse(bruto) : null
  } catch (e) {
    return null
  }
}

function gravarRegistro(registro) {
  const area = storage()
  if (!area) return false
  try {
    area.setItem(CHECKOUT_ORIGEM_STORAGE_KEY, JSON.stringify(registro))
    return true
  } catch (e) {
    return false
  }
}

function limparOrigem() {
  const area = storage()
  if (!area) return
  try {
    area.removeItem(CHECKOUT_ORIGEM_STORAGE_KEY)
  } catch (e) {
    // Sem storage não há o que limpar.
  }
}

function expirado(registro, agora) {
  const gravadoEm = Number(registro.gravadoEm)
  return !isFinite(gravadoEm) || agora - gravadoEm > CHECKOUT_ORIGEM_TTL_MS
}

// Grava a origem (fase `ida`). Recusa rota que não seja path interno — e devolve `false`
// para o chamador saber que o retorno vai cair na rota padrão do backend. A `aba` é a de
// onde o checkout saiu: no retorno para `/central-ia/creditos` (sem id de pagamento) é
// ela que separa quem desistiu da compra AVULSA (aba Adicionar) de quem voltou do checkout
// da ASSINATURA (aba Planos) — as duas rotas de retorno são a mesma.
function guardarOrigem({
  rota = null,
  superficie = CHECKOUT_SUPERFICIE.PAGINA,
  aba = null,
  agora = Date.now(),
} = {}) {
  if (!rotaInternaValida(rota)) return false
  return gravarRegistro({ rota, superficie, aba, gravadoEm: agora, fase: FASE.IDA, retorno: null })
}

// O registro vivo, ou `null`. Registro expirado, corrompido ou com rota inválida é
// APAGADO na leitura: um lixo de uma hora atrás não pode redirecionar ninguém.
function lerOrigem({ agora = Date.now() } = {}) {
  const registro = lerRegistro()
  if (!registro) return null
  if (!rotaInternaValida(registro.rota) || expirado(registro, agora)) {
    limparOrigem()
    return null
  }
  return registro
}

// Fase `volta`: o guard viu o retorno da Stripe e vai redirecionar; o payload é o que o
// modal precisa para reabrir na aba certa. Só transita a partir de `ida`.
function marcarRetorno(retorno, { agora = Date.now() } = {}) {
  const registro = lerOrigem({ agora })
  if (!registro || registro.fase !== FASE.IDA) return false
  return gravarRegistro({ ...registro, fase: FASE.VOLTA, retorno: retorno || {} })
}

// O payload da fase `volta`, apagando o registro — ou `null` se não há retorno pendente.
function consumirRetorno({ agora = Date.now() } = {}) {
  const registro = lerOrigem({ agora })
  if (!registro || registro.fase !== FASE.VOLTA) return null
  limparOrigem()
  return registro.retorno || {}
}

export {
  FASE,
  consumirRetorno,
  guardarOrigem,
  lerOrigem,
  limparOrigem,
  marcarRetorno,
  rotaEhDeCreditos,
  rotaInternaValida,
}
